import { Box, Checkbox, Theme, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import clsx from "clsx";
import _ from "lodash";
import React, { useState, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { TableCellProps, SortDirectionType } from "react-virtualized";

import Table from "../../components/Table";
import TextField from "../../components/Table/TextField";
import { eventCategoriesMap, eventDefType } from "../../eventTypes";
import { LOAD_EVENTS } from "../../reducers/events";
import { handleSaveEvent } from "../api";
import DestinationsCell from "../components/BodyCell/Destinations";
import { TagsCell } from "./BodyCell/Tags";
import { COLUMNS } from "../constants";
import { sortEvents } from "../filtering";
import { VisibilityOffIcon } from "../../components/icons/themes/materialdesign";

const useStyles = makeStyles((theme: Theme) => ({
  tableHeader: {
    color: "#444444",
    fontSize: "0.875rem",
    fontWeight: 700,
  },
  eyeIcon: {
    marginLeft: theme.spacing(1),
    color: "rgba(38, 50, 56, 0.4)",
  },
}));

///////////////////////////////////////////
// Root Component
///////////////////////////////////////////
const LibraryTable = ({
  setSelectedEvents,
  selectedEvents,
  eventDefs,
  events,
  configurableDestinations,
  toggleSelectAllEvents,
  onClickRow,
  allTags,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [sortBy, setSortBy] = useState<string | undefined>();
  const [sortDirection, setSortDirection] = useState<
    SortDirectionType | undefined
  >();

  const sortedEvents = useMemo(
    () => sortEvents(events, sortBy, sortDirection),
    [events, sortBy, sortDirection]
  );
  const eventsByID = useMemo(() => _.keyBy(sortedEvents, (e) => e.id), [
    sortedEvents,
  ]);
  const nSelected = selectedEvents.length;
  const nTotal = eventDefs.length;

  ///////////////////////////////////////////
  // Body
  ///////////////////////////////////////////
  const renderBodyCell = useCallback(
    ({ dataKey, cellData, rowData }: TableCellProps) => {
      // Tags
      const reloadEventDef = (eventDef) => {
        eventDef &&
          dispatch({
            type: LOAD_EVENTS,
            payload: eventDefs.map((e) =>
              e.id === eventDef.id ? eventDef : e
            ),
          });
      };

      const onTagsSave = (tags) => {
        handleSaveEvent({
          ...rowData,
          tags,
        }).then(reloadEventDef);
      };

      // Description
      const onEditDescription = (description) => {
        dispatch({
          type: LOAD_EVENTS,
          payload: eventDefs.map((e) =>
            e.id === rowData.id ? _.extend({}, e, { description }) : e
          ),
        });

        handleSaveEvent({
          ...rowData,
          description,
        });
      };

      // Checked
      const onToggleCheckbox = (e: React.ChangeEvent<HTMLInputElement>) =>
        setSelectedEvents((evts) =>
          e.currentTarget.checked
            ? _.uniq([...evts, rowData.id])
            : _.filter(evts, (v) => v !== rowData.id)
        );

      switch (dataKey) {
        case "type":
          return eventCategoriesMap[eventDefType(rowData)] || "";

        case "actions":
          return "";

        case "tags":
          return (
            <TagsCell
              allTags={allTags}
              activeTags={cellData}
              onSave={onTagsSave}
            />
          );

        case "description":
          return (
            <TextField
              value={cellData}
              fieldName="Description"
              multiline
              onEdit={onEditDescription}
            />
          );

        case "destinations":
          return (
            <DestinationsCell
              event={eventsByID[rowData.id]}
              configurableDestinations={configurableDestinations}
            />
          );

        case "checked":
          return (
            <Checkbox
              onClick={(e) => e.stopPropagation()}
              checked={cellData}
              onChange={onToggleCheckbox}
            />
          );

        case "name":
          return (
            <>
              <Tooltip title={cellData}>
                <span className="truncate">{cellData}</span>
              </Tooltip>
              {eventsByID[rowData.id]?.is_hidden && (
                <Tooltip title="This event is hidden">
                  <VisibilityOffIcon className={clsx(classes.eyeIcon)} />
                </Tooltip>
              )}
            </>
          );

        default:
          return (
            <Tooltip title={cellData}>
              <span className="truncate">{cellData}</span>
            </Tooltip>
          );
      }
    },
    [
      allTags,
      eventsByID,
      configurableDestinations,
      dispatch,
      eventDefs,
      setSelectedEvents,
      classes.eyeIcon,
    ]
  );

  ///////////////////////////////////////////
  // Header
  ///////////////////////////////////////////
  const renderHeaderCell = useCallback(
    ({ dataKey, label, columnData }) => {
      const atLeastOneSelected = nSelected > 0;
      const allEventsSelected = nSelected === nTotal;

      const HeaderColumn = ({ label }) => (
        <Tooltip title={columnData.toolTip} style={{ cursor: "pointer" }}>
          <span className={clsx(classes.tableHeader)}>{label}</span>
        </Tooltip>
      );

      return dataKey === "checked" ? (
        <Box
          position="absolute"
          top="50%"
          style={{ transform: "translateY(-50%)" }}
        >
          <Checkbox
            checked={atLeastOneSelected}
            indeterminate={atLeastOneSelected && !allEventsSelected}
            onChange={toggleSelectAllEvents}
          />
        </Box>
      ) : (
        <HeaderColumn label={label} />
      );
    },
    [toggleSelectAllEvents, nSelected, nTotal, classes]
  );

  return (
    <Table
      columns={COLUMNS}
      renderBodyCell={renderBodyCell}
      renderHeaderCell={renderHeaderCell}
      totalRows={events.length}
      onClickRow={onClickRow}
      rowGetter={({ index }: { index: number }) => ({
        ...sortedEvents[index],
        checked: selectedEvents.includes(sortedEvents[index].id),
      })}
      sort={({ sortBy, sortDirection }) => {
        setSortBy(sortBy);
        setSortDirection(sortDirection);
      }}
      sortBy={sortBy}
      sortDirection={sortDirection}
    />
  );
};

export { LibraryTable };
